/**
 * Strip identifiers out of a URL before it is allowed off the platform.
 *
 * The browser SDK sends URLs in two places by default — `request.url` from
 * `httpContextIntegration`, and `data.url` / `data.from` / `data.to` on every
 * xhr, fetch and history breadcrumb — and `sendDefaultPii: false` does not
 * touch either. On this platform a URL is not merely an identifier:
 *
 *   /questionnaire/<publicLinkToken>
 *   /questionnaires/submissions/by-token/<submissionToken>
 *
 * are unauthenticated capability URLs. `Api/endpoints/questionnaires.py` says
 * so in as many words — "the capability is the token in the URL" — and the
 * endpoint behind the second one takes no auth dependency at all and answers
 * with a named person's answers, email and date of birth. Anyone who can read
 * a Sentry issue could replay it.
 *
 * So this mirrors what `Api/telemetry.py` does server-side, where only the
 * route *template* survives and never the resolved path: the shape of the
 * address is kept, every value in it is replaced. Query and fragment go
 * wholesale — a token is as likely to be `?t=` as a path segment, and nothing
 * in this app needs a query string to identify which call failed.
 */

const UUID =
	/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * A long opaque segment — a base64url token rather than a route name.
 *
 * The length alone is not enough: `data-permissions`, `retention-options` and
 * `write-destinations` are all real addresses in this app and all 16+
 * characters, and redacting them would erase the one thing redaction is meant
 * to preserve, which is *which* call failed. A token also carries a digit or
 * mixed case; a kebab-case route name carries neither.
 *
 * Both orders of mixed case, not just lower-then-upper: `Abcdefghijklmnop` is a
 * perfectly ordinary base64url token and a lookahead for `[a-z].*[A-Z]` misses
 * it. A missed token is the unsafe direction, so the two classes are tested
 * independently of where they appear.
 */
const OPAQUE = /^(?=.*\d|(?=.*[a-z])(?=.*[A-Z]))[A-Za-z0-9_-]{16,}$/;

const redactSegment = (segment: string): string => {
	if (segment === '') return segment;
	if (UUID.test(segment)) return ':id';
	// Numeric ids: /coaches/members/1, /documents/12/review.
	if (/^\d+$/.test(segment)) return ':id';
	if (OPAQUE.test(segment)) return ':id';
	return segment;
};

/**
 * Returns the URL with every identifying part replaced, or undefined if it
 * cannot be parsed — dropping it entirely is the safe direction.
 */
export const redactUrl = (raw: unknown): string | undefined => {
	if (typeof raw !== 'string' || raw === '') return undefined;

	// Relative URLs are parsed against a placeholder base that is then removed,
	// so this works without `window` and stays testable in a node environment.
	const PLACEHOLDER = 'https://redacted.invalid';
	let url: URL;
	try {
		url = new URL(raw, PLACEHOLDER);
	} catch {
		return undefined;
	}

	// Anything that is not a web address is dropped rather than rewritten. A
	// non-special scheme gives an opaque origin, which `URL` reports as the
	// literal string "null" — concatenating that produced `nulljane@example.org`
	// for a mailto:, forwarding the address it was supposed to remove. Nothing
	// this app calls is anything but http(s), so dropping is free.
	if (url.protocol !== 'http:' && url.protocol !== 'https:') return undefined;

	const path = url.pathname.split('/').map(redactSegment).join('/');
	const origin = url.origin === PLACEHOLDER ? '' : url.origin;
	return `${origin}${path}`;
};

/** A UUID anywhere, not anchored — for sweeping free text rather than a segment. */
const UUID_ANYWHERE =
	/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/** A whole http(s) URL sitting inside a larger string. */
const URL_ANYWHERE = /https?:\/\/[^\s'"`<>)\]]+/gi;

/**
 * Redact identifiers out of free text — an exception message, a frame filename.
 *
 * Narrower than `redactUrl` on purpose. That one knows every segment is part of
 * an address and can redact numeric and opaque ones; this one runs over English
 * and JavaScript, where the same rules would eat the useful part: `Cannot read
 * properties of undefined (reading 'availableMeasurements')` loses its only
 * identifying word to a 16-plus-characters-and-mixed-case rule.
 *
 * So it redacts exactly two shapes, and both are unambiguous in prose: a whole
 * URL, and a bare UUID. That is not an accident of convenience — the two
 * capability tokens on this platform, `publicLinkToken` and `submissionToken`,
 * are both `PGUUID` columns (`Api/model/questionnaire.py`), so a UUID sweep
 * covers the thing this exists to stop, wherever in the sentence it appears.
 *
 * The residual is stated rather than hidden: a secret that is neither a URL nor
 * a UUID would survive in a message. Nothing on this platform mints one today.
 */
export const redactText = (raw: unknown): string | undefined => {
	if (typeof raw !== 'string' || raw === '') return undefined;
	return raw
		.replace(URL_ANYWHERE, (url) => redactUrl(url) ?? ':url')
		.replace(UUID_ANYWHERE, ':id');
};
